import { useState } from 'react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';

interface Draft {
  id: string;
  productId: string;
  quantity: number;
  performedBy: string | null;
  createdAt: Date;
  warnings: string[];
  product: { name: string };
}

interface PendingDraftsCardProps {
  type: 'MORNING' | 'EVENING';
  drafts: Draft[];
  onApprove: (acknowledgeWarnings: boolean) => void;
  onReject: (reason: string) => void;
  isApproving: boolean;
}

export function PendingDraftsCard({
  type,
  drafts,
  onApprove,
  onReject,
  isApproving
}: PendingDraftsCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const hasWarnings = drafts.some(d => d.warnings?.length > 0);
  const submittedBy = drafts[0]?.performedBy || 'Staff';
  const productCount = drafts.length;
  const warningCount = drafts.filter(d => d.warnings?.length > 0).length;

  const handleApprove = () => {
    haptic.medium();
    onApprove(acknowledged);
  };

  const handleReject = () => {
    haptic.medium();
    onReject(rejectReason);
    setShowReject(false);
    setRejectReason('');
  };

  return (
    <div className="border-2 border-yellow-500/50 bg-yellow-500/5 rounded-2xl overflow-hidden">
      <div
        className="p-4 flex items-center gap-4 cursor-pointer active:bg-white/5 active:scale-[0.99] transition-all"
        onClick={() => { haptic.light(); setExpanded(!expanded); }}
      >
        <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center text-3xl">
          ⏳
        </div>

        <div className="flex-1">
          <h3 className="font-bold text-white text-lg">
            {type === 'MORNING' ? 'Morning' : 'Evening'} Count - Pending Approval
          </h3>
          <p className="text-sm text-gray-400">
            Submitted by {submittedBy} • {productCount} products
            {warningCount > 0 && <span className="text-yellow-500"> • {warningCount} warnings</span>}
          </p>
        </div>

        <div className="text-2xl text-gray-400">
          {expanded ? '▼' : '▶'}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-700/50 p-4 bg-dark-lighter/50 space-y-4">
          {/* Product list summary */}
          <div className="max-h-48 overflow-y-auto space-y-2">
            {drafts.map(draft => (
              <div
                key={draft.id}
                className={cn(
                  'flex justify-between text-sm p-2 rounded-lg',
                  draft.warnings?.length > 0 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-dark-card'
                )}
              >
                <span className="text-white">{draft.product.name}</span>
                <span className="text-gray-400">{draft.quantity}g</span>
              </div>
            ))}
          </div>

          {/* Warning acknowledgment */}
          {hasWarnings && (
            <label className="flex items-center gap-3 cursor-pointer group p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => { haptic.light(); setAcknowledged(e.target.checked); }}
                className="w-4 h-4 accent-yellow-500"
              />
              <span className="text-sm text-yellow-500 group-hover:text-yellow-400 transition-colors">
                I have reviewed the {warningCount} warning(s)
              </span>
            </label>
          )}

          {/* Action buttons */}
          {!showReject ? (
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                disabled={isApproving || (hasWarnings && !acknowledged)}
                className={cn(
                  'flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all active:scale-[0.97]',
                  isApproving || (hasWarnings && !acknowledged)
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-success hover:bg-success/80 text-white'
                )}
              >
                ✓ {isApproving ? 'Approving...' : 'Approve'}
              </button>
              <button
                onClick={() => { haptic.light(); setShowReject(true); }}
                className="flex-1 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all active:scale-[0.97]"
              >
                ✕ Reject
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full bg-dark-card border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { haptic.light(); setShowReject(false); setRejectReason(''); }}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-all active:scale-[0.97]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  className={cn(
                    'flex-1 py-3 rounded-xl font-semibold transition-all active:scale-[0.97]',
                    !rejectReason.trim()
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-500 text-white'
                  )}
                >
                  Confirm Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
